import { useEffect, useState } from 'react'
import { Typography, TextField, Avatar } from '@mui/material'
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { ColorTokens, RadiusTokens } from 'src/feature/theme/model/Palette'
import { useUserStore } from 'src/feature/user/store/user.store'
import { useMyTeamStore } from '../../store/my-team.store'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppAvatar } from 'src/component/common/analog/molecule/AppAvatar'
import { API_BASE_URL } from 'src/util/constant'

type PerformanceDetailsPaneProps = {
  templateId: string | null
  templateName?: string
}

export const PerformanceDetailsPane = observer(({ templateId, templateName }: PerformanceDetailsPaneProps) => {
  const { translation } = useLocalizationStore()
  const performanceReviewTranslation = translation.pages.myTeam.performanceReview
  const myTeamStore = useMyTeamStore()
  const userStore = useUserStore()
  const me = userStore.userData
  const myName = `${me?.firstName ?? ''} ${me?.lastName ?? ''}`.trim() || 'You'

  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null)
  const [privateNote, setPrivateNote] = useState('')
  const [showPrivateNote, setShowPrivateNote] = useState(false)
  const [answerComments, setAnswerComments] = useState<Record<string, string>>({})

  const fullTemplate = myTeamStore.currentPerformanceReviewTemplate
  const participantUsers = myTeamStore.currentMeetingParticipants
  const userResponses = myTeamStore.currentUserResponses
  const questions = fullTemplate?.questions ?? []

  useEffect(() => {
    if (templateId) {
      myTeamStore.currentPerformanceReviewTemplate = null
      myTeamStore.currentMeetingParticipants = []
      myTeamStore.currentUserResponses = []
      myTeamStore.currentUserComment = ''
      myTeamStore.currentAnswerComments = []
      setSelectedParticipantId(null)
      myTeamStore.getMeetingById(templateId)
      myTeamStore.getMeetingParticipants(templateId)
      myTeamStore.currentPerformanceReviewInsights = null
      myTeamStore.getPerformanceReviewInsights(templateId)
    }
  }, [templateId, myTeamStore])

  const participants = participantUsers.map((user) => {
    return {
      id: user.id,
      name: `${user.firstName} ${user.lastName}`,
      avatarUrl: user.avatarUrl,
      completion: user.hasCompleted ? 100 : 0,
    }
  }).sort((a, b) => b.completion - a.completion)

  const activeParticipant = participants.find(p => p.id === selectedParticipantId) ?? participants[0]
  const activeParticipantId = activeParticipant?.id

  useEffect(() => {
    if (templateId && activeParticipantId) {
      myTeamStore.getUserResponses(templateId, activeParticipantId)
      myTeamStore.getComment(templateId, activeParticipantId)
      myTeamStore.getAnswerComments(templateId, activeParticipantId)
    }
  }, [templateId, activeParticipantId, myTeamStore])

  useEffect(() => {
    setPrivateNote(myTeamStore.currentUserComment)
  }, [myTeamStore.currentUserComment])

  useEffect(() => {
    const map: Record<string, string> = {}
    myTeamStore.currentAnswerComments.forEach((c) => {
      map[c.questionId] = c.content
    })
    setAnswerComments(map)
  }, [myTeamStore.currentAnswerComments])

  const handleNoteBlur = () => {
    if (templateId && activeParticipantId) {
      myTeamStore.saveComment(templateId, activeParticipantId, privateNote)
    }
  }

  const handleAnswerCommentChange = (questionId: string, value: string) => {
    setAnswerComments(prev => ({ ...prev, [questionId]: value }))
  }

  const handleAnswerCommentBlur = (questionId: string) => {
    if (templateId && activeParticipantId) {
      myTeamStore.saveAnswerComment(templateId, activeParticipantId, questionId, answerComments[questionId] || '')
    }
  }

  const findResponse = (questionId: string) => {
    return userResponses.find((r) => {
      const qId = typeof r.question === 'string' ? r.question : r.question.id

      return qId === questionId
    })
  }

  if (!templateId) {
    return (
      <AppBox sx={styles.emptyPanel}>
        <Typography sx={styles.placeholderText}>{performanceReviewTranslation.selectTemplate()}</Typography>
      </AppBox>
    )
  }

  return (
    <AppPaper sx={styles.panel}>
      <AppBox sx={styles.inner}>
        <AppBox sx={styles.topRow}>
          <Typography sx={styles.templateTitle}>{templateName}</Typography>
          <NoteAltOutlinedIcon
            sx={styles.noteToggle}
            onClick={() => setShowPrivateNote(!showPrivateNote)}
          />
        </AppBox>

        <AppBox sx={styles.header}>
          <AppAvatar
            src={
              activeParticipant?.avatarUrl
                ? `${API_BASE_URL}${activeParticipant.avatarUrl}`
                : undefined
            }
            outerProps={{
              squareSize: 44,
              sx: styles.headerAvatar,
            }}
          />
          <Typography sx={styles.headerName}>
            {activeParticipant ? activeParticipant.name : '-'}
          </Typography>
        </AppBox>

        <AppBox sx={styles.chipsRow}>
          {participants.map((p) => {
            const isSelected = p.id === activeParticipantId
          
            return p.completion === 100 ? (
              <AppBox
                key={p.id}
                sx={{
                  ...styles.chipDone,
                  ...(isSelected && styles.chipDoneSelected),
                }}
                onClick={() => setSelectedParticipantId(p.id)}
              >
                <Typography sx={styles.chipDoneName}>{p.name}</Typography>
                <CheckCircleOutlineIcon sx={styles.chipCheck} />
              </AppBox>
            ) : (
              <AppBox
                key={p.id}
                sx={{
                  ...styles.chip,
                  ...(isSelected && styles.chipSelected),
                }}
                onClick={() => setSelectedParticipantId(p.id)}
              >
                <Typography sx={styles.chipName}>{p.name}</Typography>
                <Typography sx={styles.chipPct}>{p.completion}%</Typography>
              </AppBox>
            )
          })}
        </AppBox>

        {showPrivateNote && (
          <AppBox sx={styles.noteBlock}>
            <AppBox sx={styles.noteHeader}>
              <Typography sx={styles.noteLabel}>{performanceReviewTranslation.meetingDetail.privateComment()}</Typography>
              <Typography sx={styles.noteHint}>{performanceReviewTranslation.meetingDetail.privateCommentHint()}</Typography>
            </AppBox>
            <TextField
              placeholder={performanceReviewTranslation.meetingDetail.privateCommentPlaceholder()}
              multiline
              rows={3}
              value={privateNote}
              onChange={e => setPrivateNote(e.target.value)}
              onBlur={handleNoteBlur}
              fullWidth
              sx={styles.noteField}
            />
          </AppBox>
        )}

        <AppBox sx={styles.questionList}>
          {questions.map((q, idx) => {
            const response = findResponse(q.id)

            return (
              <AppBox key={q.id} sx={styles.questionBlock}>
                <Typography sx={styles.questionLabel}>
                  {performanceReviewTranslation.meetingDetail.question()} {String(idx + 1).padStart(2, '0')}
                </Typography>
                <Typography sx={styles.questionText}>{q.questionText}</Typography>
                <AppBox sx={styles.answerBubble}>
                  <AppBox sx={styles.answerLabelPill}>{performanceReviewTranslation.meetingDetail.userAnswers()}</AppBox>
                  <Typography sx={styles.answerText}>
                    {response ? response.responseValue : performanceReviewTranslation.meetingDetail.noResponse()}
                  </Typography>
                </AppBox>
                <AppBox sx={styles.commentRow}>
                  <Avatar src={userStore.avatar} sx={styles.commentAvatar} />
                  <AppBox sx={styles.commentCard}>
                    <Typography sx={styles.commentName}>{myName}</Typography>
                    <TextField
                      placeholder={performanceReviewTranslation.meetingDetail.commentPlaceholder()}
                      fullWidth
                      multiline
                      minRows={1}
                      value={answerComments[q.id] || ''}
                      onChange={e => handleAnswerCommentChange(q.id, e.target.value)}
                      onBlur={() => handleAnswerCommentBlur(q.id)}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      sx={styles.commentField}
                    />
                  </AppBox>
                </AppBox>
              </AppBox>
            )
          })}
        </AppBox>
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  panel: {
    flex: 1,
    padding: 15,
    borderRadius: '12px',
    minHeight: 400,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    border: 'none',
  },
  emptyPanel: {
    flex: 1,
    padding: 15,
    borderRadius: '12px',
    minHeight: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    fontSize: 12,
    color: 'text.secondary',
  },
  inner: {
    width: '100%',
    flexDirection: 'column',
    gap: 15,
  },
  topRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noteToggle: {
    fontSize: 26,
    color: ColorTokens.secondary500,
    cursor: 'pointer',
  },
  templateTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: ColorTokens.secondary500,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerAvatar: {
    width: 44,
    height: 44,
    backgroundColor: ColorTokens.primary200,
    color: ColorTokens.secondary500,
  },
  headerName: {
    fontSize: 16,
    fontWeight: 600,
    color: ColorTokens.secondary500,
  },
  chipsRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 8,
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: 6,
    minWidth: 0,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: RadiusTokens.pill,
    border: '1px solid',
    borderColor: ColorTokens.border100,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      borderColor: ColorTokens.secondary500,
      backgroundColor: `${ColorTokens.secondary500}08`,
    },
  },
  chipName: {
    fontSize: 12,
    fontWeight: 600,
    color: ColorTokens.secondary500,
  },
  chipPct: {
    fontSize: 11,
    color: 'text.secondary',
  },
  chipDone: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px 6px 14px',
    borderRadius: RadiusTokens.pill,
    backgroundColor: ColorTokens.primary400,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: ColorTokens.primary500,
    },
  },
  chipSelected: {
    borderColor: ColorTokens.primary200,
    border: '2px solid',
  },
  chipDoneSelected: {
    border: '2px solid',
    borderColor: ColorTokens.primary200,
  },
  chipDoneName: {
    fontSize: 12,
    fontWeight: 600,
    color: ColorTokens.neutral100,
  },
  chipCheck: {
    fontSize: 18,
    color: ColorTokens.neutral100,
  },
  noteBlock: {
    width: '100%',
    flexDirection: 'column',
    gap: 8,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
  },
  noteLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1px',
    color: ColorTokens.primary500,
  },
  noteHint: {
    fontSize: 11,
    color: 'text.secondary',
  },
  noteField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: 13,
      backgroundColor: `${ColorTokens.primary200}15`,
      '& fieldset': {
        borderColor: ColorTokens.border100,
      },
    },
    '& .MuiOutlinedInput-input': {
      '&::placeholder': {
        color: ColorTokens.neutral800,
        opacity: 1,
      },
    },
  },
  questionList: {
    width: '100%',
    flexDirection: 'column',
    gap: 14,
  },
  questionBlock: {
    width: '100%',
    flexDirection: 'column',
    gap: 8,
    padding: 16,
    borderRadius: '12px',
    border: '1px solid',
    borderColor: ColorTokens.border100,
    backgroundColor: ColorTokens.neutral100,
  },
  questionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1px',
    color: ColorTokens.primary500,
  },
  questionText: {
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '0.3px',
    color: ColorTokens.secondary500,
    marginBottom: 12,
  },
  answerBubble: {
    width: '100%',
    padding: '20px 14px 12px 14px',
    borderRadius: '10px',
    backgroundColor: ColorTokens.neutral200,
    flexDirection: 'column',
    gap: 8,
    position: 'relative',
  },
  answerLabelPill: {
    position: 'absolute',
    top: '-10px',
    left: '14px',
    padding: '3px 10px',
    borderRadius: RadiusTokens.pill,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.5px',
    backgroundColor: ColorTokens.primary500,
    color: ColorTokens.neutral100,
  },
  answerText: {
    fontSize: 13,
    color: ColorTokens.secondary500,
    lineHeight: 1.5,
    fontStyle: 'italic',
  },
  commentRow: {
    width: '100%',
    marginTop: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  commentAvatar: {
    width: 36,
    height: 36,
    backgroundColor: ColorTokens.primary200,
    color: ColorTokens.secondary500,
    flexShrink: 0,
  },
  commentCard: {
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: ColorTokens.border100,
    backgroundColor: ColorTokens.neutral100,
  },
  commentName: {
    fontSize: 14,
    fontWeight: 600,
    color: ColorTokens.secondary500,
  },
  commentField: {
    '& .MuiInputBase-input': {
      fontSize: 13,
      color: ColorTokens.neutral800,
      lineHeight: 1.5,
      '&::placeholder': {
        color: ColorTokens.neutral800,
        opacity: 1,
      },
    },
  },
}
